/**
 * LazyBlock — viewport-based lazy rendering.
 *
 * Only mounts content when the block enters the viewport (with 600px margin).
 * Once rendered, content is kept mounted to avoid re-mount costs on
 * fast scroll-back. Uses requestIdleCallback for deferred mounting.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";

const LAZY_BLOCK_ROOT_MARGIN = "600px 0px";
const LAZY_BLOCK_ROOT_MARGIN_PX = 600;
const useIsomorphicLayoutEffect =
    typeof window === "undefined" ? useEffect : useLayoutEffect;

function isNearViewport(element: HTMLElement): boolean {
    if (typeof window === "undefined") return true;
    const rect = element.getBoundingClientRect();
    const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
    return (
        rect.bottom >= -LAZY_BLOCK_ROOT_MARGIN_PX &&
        rect.top <= viewportHeight + LAZY_BLOCK_ROOT_MARGIN_PX
    );
}

export function LazyBlock({
    children,
    estimatedHeight = 60,
}: {
    children: React.ReactNode;
    estimatedHeight?: number;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const measuredHeight = useRef(0);
    // Once rendered, never unmount — avoids re-mount cost on fast scroll-back
    const hasRendered = useRef(false);
    const [visible, setVisible] = useState(
        () =>
            typeof window === "undefined" ||
            typeof IntersectionObserver === "undefined",
    );

    useIsomorphicLayoutEffect(() => {
        const el = ref.current;
        if (!el || visible) return;
        if (isNearViewport(el)) {
            hasRendered.current = true;
            setVisible(true);
        }
    }, [visible]);

    useEffect(() => {
        const el = ref.current;
        if (!el || visible) return;
        if (typeof IntersectionObserver === "undefined") {
            hasRendered.current = true;
            setVisible(true);
            return;
        }
        let idleHandle: number | undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Defer mount to idle time — if user scrolls past quickly, the
                    // callback is cancelled before the component ever mounts.
                    if (typeof requestIdleCallback !== "undefined") {
                        idleHandle = requestIdleCallback(() => {
                            hasRendered.current = true;
                            setVisible(true);
                        });
                    } else {
                        hasRendered.current = true;
                        setVisible(true);
                    }
                } else {
                    // Cancel pending idle mount if user scrolled past before it fired
                    if (idleHandle != null && typeof cancelIdleCallback !== "undefined") {
                        cancelIdleCallback(idleHandle);
                        idleHandle = undefined;
                    }
                    // Record measured height for stable placeholder
                    const h = el.offsetHeight;
                    if (h > 0) measuredHeight.current = h;
                    // Keep content if it was ever rendered (avoids teardown/rebuild)
                    if (!hasRendered.current) {
                        setVisible(false);
                    }
                }
            },
            { rootMargin: LAZY_BLOCK_ROOT_MARGIN },
        );
        observer.observe(el);
        return () => {
            observer.disconnect();
            if (idleHandle != null && typeof cancelIdleCallback !== "undefined") {
                cancelIdleCallback(idleHandle);
            }
        };
    }, [visible]);

    return (
        <div
            ref={ref}
            style={
                visible
                    ? undefined
                    : { height: measuredHeight.current || estimatedHeight }
            }
        >
            {visible ? children : null}
        </div>
    );
}
